import { useTracker } from '@archipro-website/tracker';
import { useCountryContext } from '@archipro-website/localisation';
import { ReactComponent as InstagramIcon } from '@rocco/icons/svg/brand/instagram.svg';
import { ReactComponent as FacebookIcon } from '@rocco/icons/svg/brand/facebook.svg';
import { ReactComponent as LinkedinIcon } from '@rocco/icons/svg/brand/linkedin.svg';
import { useUser } from '@modules/user';
import { useRootData } from '~/modules/root';
import { getUrlPrefix } from '~/modules/user/component/share-modal/util/getUrlPrefix';
import { addUtmDataToShareUrl } from '~/modules/user/component/share-modal/util/addUtmDataToShareUrl';
import { copyToClipboard } from '~/modules/social-sharing/utils/sharing';
import type { SocialMediaShareType } from '~/modules/moodboards/config';

interface RevampShareLinksProps {
    /** The article's own path, e.g. `/article/my-article`. */
    shareLink: string;
    /** Article the share is attributed to — the UTM content id. */
    articleID: number;
    articleTitle: string;
}

const circleClass =
    'flex size-[2.125rem] cursor-pointer items-center justify-center rounded-full border border-solid border-[#181818] bg-transparent p-0 transition-opacity hover:opacity-70';

const iconClass = 'size-[1rem] fill-[#181818]';

/**
 * The networks' share composers. Built here rather than through react-share,
 * which only survives this app's bundling on the client — the article page
 * renders this row on the server.
 */
const buildShareUrl = {
    facebook: (url: string) =>
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    linkedin: (url: string) =>
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
};

/**
 * Shares the article to Instagram, Facebook and LinkedIn (Figma 2497:77394).
 * Carries the same UTM tagging and `Share` / `ShareSubmit` events as the legacy
 * share modal so the series stays continuous across the two article pages.
 */
export const RevampShareLinks = ({
    shareLink,
    articleID,
    articleTitle,
}: RevampShareLinksProps) => {
    const tracker = useTracker();
    const user = useUser();
    const { env } = useRootData();
    const { code } = useCountryContext();

    // The canonical public URL, not the current origin: a share from a branch
    // deploy or a preview host has to land on the live article.
    const shareUrl = shareLink.startsWith('http')
        ? shareLink
        : `${getUrlPrefix(code, env)}${shareLink}`;

    const withUtm = (socialType: SocialMediaShareType) =>
        addUtmDataToShareUrl({
            url: shareUrl,
            socialType,
            pageID: articleID,
            user,
        });

    const trackShare = (socialType: SocialMediaShareType) => {
        tracker.log('Share', {
            url: new URL(shareUrl),
            targetTracker: ['archiproTracker', 'ga4Tracker'],
            data: {
                ExtraData: socialType,
            },
            ga4Data: { socialType },
        });
        tracker.log('ShareSubmit', {
            url: new URL(window.location.href),
            targetTracker: ['archiproTracker', 'ga4Tracker'],
        });
    };

    // Instagram has no web share intent, so hand the link to the OS share
    // sheet where there is one (mobile, where the app can receive it) and fall
    // back to the clipboard on desktop.
    const shareToInstagram = async () => {
        trackShare('instagram');
        const url = withUtm('instagram');

        if (typeof navigator !== 'undefined' && navigator.share) {
            try {
                await navigator.share({ title: articleTitle, url });
            } catch {
                // Dismissing the share sheet rejects — nothing to recover from.
            }
            return;
        }

        await copyToClipboard(url);
    };

    return (
        <ul className="m-0 flex list-none items-center gap-[0.375rem] p-0">
            <li>
                <button
                    type="button"
                    aria-label="Share on Instagram"
                    onClick={() => void shareToInstagram()}
                    className={circleClass}
                >
                    <InstagramIcon className={iconClass} aria-hidden="true" />
                </button>
            </li>
            <li>
                <a
                    href={buildShareUrl.facebook(withUtm('facebook'))}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Share on Facebook"
                    onClick={() => trackShare('facebook')}
                    className={circleClass}
                >
                    <FacebookIcon className={iconClass} aria-hidden="true" />
                </a>
            </li>
            <li>
                <a
                    href={buildShareUrl.linkedin(withUtm('linkedin'))}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Share on LinkedIn"
                    onClick={() => trackShare('linkedin')}
                    className={circleClass}
                >
                    <LinkedinIcon className={iconClass} aria-hidden="true" />
                </a>
            </li>
        </ul>
    );
};
